import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { usePaywallPreview } from "@/hooks/usePaywallPreview";
import { useLencoPayment } from "@/hooks/useLencoPayment";
import { useProCount } from "@/hooks/useProCount";
import { useFoundingSpotsRemaining } from "@/hooks/useFoundingSpotsRemaining";
import { TransparencyModal } from "./TransparencyModal";
import { trackConversion } from "@/lib/conversionTracking";
import type { OpenPaywallSource, PaywallPlan } from "@/types/paywall";

export type { PaywallPlan };

const PRO_SOLO_MONTHLY = 900;
const PRO_SOLO_ANNUAL = 8500;
const PRO_SOLO_ANNUAL_SAVINGS = PRO_SOLO_MONTHLY * 12 - PRO_SOLO_ANNUAL;
const PRO_SOLO_FOUNDING_MONTHLY = 750;

const PRO_GROWTH_MONTHLY = 1800;
const PRO_GROWTH_ANNUAL = 17000;
const PRO_GROWTH_ANNUAL_SAVINGS = PRO_GROWTH_MONTHLY * 12 - PRO_GROWTH_ANNUAL;
const PRO_GROWTH_FOUNDING_MONTHLY = 1400;

function formatK(n: number): string {
  return `K${n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

type PaywallCopy = {
  headline: string;
  description: string;
  openingLine: string;
  followUpLine: string;
  insightLine: string;
  ctaLabel: string;
};

function getPaywallCopy(
  source: OpenPaywallSource | undefined,
  {
    ownerName,
    businessName,
    survivalDays,
    deadStockValue,
    biCount,
  }: {
    ownerName: string | null;
    businessName: string;
    survivalDays: number;
    deadStockValue: number;
    biCount: number;
  }
): PaywallCopy {
  const ownerPrefix = ownerName ? `${ownerName}, ` : "";
  const insightsLine = biCount > 0
    ? `${biCount} insight${biCount === 1 ? "" : "s"} ${biCount === 1 ? "is" : "are"} already ready for ${businessName}.`
    : "Add products and sales to unlock deeper business insights in Pro.";

  switch (source) {
    case "survival_days":
      return {
        headline: `${ownerPrefix}upgrade to Pro to view your full cash runway breakdown.`,
        description: "Industry-standard cash flow visibility helps you see how long current cash can support operations and where to act early.",
        openingLine: `${businessName} currently has ${survivalDays} days of cash runway.`,
        followUpLine: "Pro unlocks the full breakdown behind this number so you can plan corrective action with confidence.",
        insightLine: insightsLine,
        ctaLabel: "Unlock Cash Flow Insights",
      };
    case "dead_stock_kwacha":
      return {
        headline: `${ownerPrefix}upgrade to Pro to see the full value tied up in dead stock.`,
        description: "Strong inventory teams track slow-moving stock in currency, not just item counts, so they can protect working capital.",
        openingLine: `${formatK(deadStockValue)} is currently tied up in dead stock.`,
        followUpLine: "Pro shows the Kwacha impact so you can discount, bundle, or reorder with better control.",
        insightLine: insightsLine,
        ctaLabel: "Unlock Dead Stock Value",
      };
    case "export_report":
      return {
        headline: `${ownerPrefix}upgrade to Pro to export professional monthly reports.`,
        description: "Executive-ready reporting is the standard for lender reviews, partner updates, and disciplined monthly management.",
        openingLine: "Export polished reports with P&L, cash flow, inventory performance, and your executive summary.",
        followUpLine: "Pro gives you a cleaner way to present the numbers behind your business decisions.",
        insightLine: insightsLine,
        ctaLabel: "Unlock Report Export",
      };
    case "history_limit":
    case "month2_trends":
      return {
        headline: `${ownerPrefix}upgrade to Pro to unlock historical reporting and trends.`,
        description: "Month-on-month analysis is an industry-standard way to spot performance changes before they become cash problems.",
        openingLine: "Compare past months, review patterns over time, and make decisions using trend data instead of snapshots alone.",
        followUpLine: "Pro gives you the reporting depth needed for real operating reviews.",
        insightLine: insightsLine,
        ctaLabel: "Unlock Historical Reports",
      };
    case "product_limit":
      return {
        headline: `${ownerPrefix}upgrade to Pro to keep your inventory records complete.`,
        description: "Once a business moves beyond a starter catalog, industry-standard inventory control means tracking every item accurately.",
        openingLine: "You have reached the Free plan inventory limit.",
        followUpLine: "Upgrade to keep adding products, stock levels, and pricing data beyond your first 5 items.",
        insightLine: deadStockValue > 0 ? `${formatK(deadStockValue)} is already sitting in slow-moving stock.` : insightsLine,
        ctaLabel: "Upgrade for More Inventory",
      };
    case "location_limit":
      return {
        headline: `${ownerPrefix}upgrade to Pro to manage stock across locations.`,
        description: "Multi-location inventory control is the standard next step when your operations spread beyond a single stockroom.",
        openingLine: "Add branches, assign products by location, and keep counts accurate where the stock actually sits.",
        followUpLine: "Pro helps you move from one-store tracking to structured branch control.",
        insightLine: insightsLine,
        ctaLabel: "Unlock Location Management",
      };
    case "transport_logistics":
      return {
        headline: `${ownerPrefix}upgrade to Pro to access Transport & Logistics.`,
        description: "Industry-standard retail operations track transport spend alongside stock, purchasing, and daily operating costs.",
        openingLine: "Record deliveries, monitor logistics costs, and tighten control over how inventory moves through the business.",
        followUpLine: "Pro makes transport data part of the same decision system as your stock and cash flow.",
        insightLine: insightsLine,
        ctaLabel: "Unlock Transport & Logistics",
      };
    case "business_intelligence":
      return {
        headline: `${ownerPrefix}upgrade to Pro for profit-by-product, reorder timing, and dead stock scenarios.`,
        description: "Decision support goes beyond recording sales: see which SKUs actually make money, when to order, and capital tied up in slow movers.",
        openingLine: "Rank products by gross margin, get actionable reorder prompts using your velocity and supplier lead times, and surface liquidation scenarios.",
        followUpLine: "Pro Solo and Pro Growth both unlock the full Stock intelligence page.",
        insightLine: insightsLine,
        ctaLabel: "Unlock Stock Intelligence",
      };
    case "reorder_intelligence":
      return {
        headline: `${ownerPrefix}unlock automated reorder timing with Pro.`,
        description:
          "Pro uses your sales velocity and supplier lead times so you can order before you run out—not after.",
        openingLine:
          "See days of stock left, suggested order quantities, and clear alerts tied to how long your suppliers usually take.",
        followUpLine: "Pro Solo and Pro Growth both include full reorder intelligence in the app.",
        insightLine: insightsLine,
        ctaLabel: "Unlock Reorder Intelligence",
      };
    case "staff_performance":
      return {
        headline: `${ownerPrefix}see your whole team’s performance with Pro.`,
        description: "Owners need visibility into who is selling and who is logging expenses — without surveillance-style detail.",
        openingLine:
          "Pro Solo and Pro Growth both include the full staff table: sales and expense leaders, rankings, and active vs quiet status from recorded activity.",
        followUpLine: "Upgrade if you run a multi-person shop and want admin-level team metrics in one place.",
        insightLine: insightsLine,
        ctaLabel: "Unlock Staff Dashboard",
      };
    case "returning_banner":
      return {
        headline: `${ownerPrefix}upgrade to Pro to view your latest business insights.`,
        description: "Your BI Analyst has already found new signals worth reviewing, and Pro is where the full recommendations are revealed.",
        openingLine: `${biCount} new insight${biCount === 1 ? "" : "s"} ${biCount === 1 ? "is" : "are"} ready for ${businessName}.`,
        followUpLine: "Open the full analysis, understand what changed, and act faster with Pro.",
        insightLine: `${businessName} currently has ${survivalDays} days of cash runway and ${formatK(deadStockValue)} in dead stock.`,
        ctaLabel: "Unlock My Insights",
      };
    case "grace":
      return {
        headline: `${ownerPrefix}renew Pro to keep your business tools live.`,
        description: "The best subscription recovery flows reduce disruption by restoring access before reporting and analysis are interrupted.",
        openingLine: "Renew now to keep reports, BI insights, exports, and paywalled tools available to your team.",
        followUpLine: "A quick renewal protects continuity and keeps your operating history intact.",
        insightLine: insightsLine,
        ctaLabel: "Renew Pro Access",
      };
    case "upgrade":
      return {
        headline: `${ownerPrefix}choose the Pro plan that fits your business.`,
        description: "A good upgrade path matches your current operating complexity while leaving room to grow.",
        openingLine: "Start with Pro Solo for one location or choose Pro Growth for more advanced multi-location control.",
        followUpLine: "Both plans unlock deeper reporting, stronger inventory visibility, and better decision support.",
        insightLine: insightsLine,
        ctaLabel: "Choose My Pro Plan",
      };
    case "share":
      return {
        headline: `${ownerPrefix}upgrade to Pro to share business performance with confidence.`,
        description: "Structured sharing and reporting are standard when teams, partners, or advisors need the same numbers.",
        openingLine: "Pro gives you the reporting depth needed to present cleaner, more reliable business information.",
        followUpLine: "Move beyond basic access and share insights backed by full reporting.",
        insightLine: insightsLine,
        ctaLabel: "Unlock Professional Reporting",
      };
    case "upgrade_cta":
    default:
      return {
        headline: `${ownerPrefix}upgrade to Pro to unlock your full business picture.`,
        description: "Serious retailers use structured reporting, inventory discipline, and visibility into cash flow to make better decisions faster.",
        openingLine: `${businessName} currently has ${survivalDays} days of cash runway.`,
        followUpLine: `${formatK(deadStockValue)} is tied up in dead stock that may need action.`,
        insightLine: insightsLine,
        ctaLabel: "Unlock My Business Intelligence",
      };
  }
}

interface PaywallModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source?: OpenPaywallSource;
}

export function PaywallModal({ open, onOpenChange, source }: PaywallModalProps) {
  const { data, loading } = usePaywallPreview();
  const { startPayment, loading: paymentLoading } = useLencoPayment();
  const proCount = useProCount();
  const foundingSpots = useFoundingSpotsRemaining();
  const [transparencyOpen, setTransparencyOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PaywallPlan>("pro_solo");
  const [isAnnual, setIsAnnual] = useState(false);
  const [useFounding, setUseFounding] = useState(false);

  const p = data;
  const survivalDays = p?.survivalDays ?? 0;
  const deadStockValue = p?.deadStockValue ?? 0;
  const biCount = p?.biRecommendationsCount ?? 0;
  const ownerName = (p?.ownerName ?? "").trim() || null;
  const businessName = (p?.businessName ?? "").trim() || "your shop";
  const copy = getPaywallCopy(source, {
    ownerName,
    businessName,
    survivalDays,
    deadStockValue,
    biCount,
  });

  const showFounding = typeof foundingSpots === "number" && foundingSpots > 0;
  const effectiveFounding = showFounding && useFounding;

  useEffect(() => {
    if (!open) return;
    setSelectedPlan("pro_solo");
    setIsAnnual(false);
    setUseFounding(false);
  }, [open, source]);

  const handleUnlock = () => {
    trackConversion("upgrade_initiated", {
      plan: selectedPlan,
      billing: isAnnual ? "annual" : "monthly",
      founding: effectiveFounding,
    });
    startPayment({
      plan: selectedPlan,
      isAnnual,
      isFounding: effectiveFounding,
    });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent data-testid="paywall-modal" className="max-w-[420px] w-[95vw] sm:max-w-lg p-6 space-y-5 max-h-[90vh] overflow-y-auto">
          <DialogHeader className="space-y-2">
            <DialogTitle className="text-xl font-display font-bold text-center">
              {copy.headline}
            </DialogTitle>
            <DialogDescription className="text-xs text-center text-muted-foreground">
              {copy.description}
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-8 gap-3">
              <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-muted-foreground">Loading your data…</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-2 text-sm">
                <p className="sr-only text-foreground" data-testid="paywall-shop-name">{businessName}</p>
                <p className="text-foreground" data-testid="paywall-survival-days-value">
                  {copy.openingLine}
                </p>
                <p className="text-foreground" data-testid="paywall-dead-stock-value">
                  {copy.followUpLine}
                </p>
                <p className="text-foreground" data-testid="paywall-insight-count">
                  {copy.insightLine}
                </p>
              </div>

              <p className="text-sm font-medium text-foreground">Choose your plan</p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setSelectedPlan("pro_solo")}
                  className={`rounded-lg border-2 p-4 text-left transition-colors ${
                    selectedPlan === "pro_solo"
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-muted-foreground/50"
                  }`}
                >
                  <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">Most Popular</span>
                  <p className="font-bold mt-0.5">Pro Solo</p>
                  <p className="text-xs text-muted-foreground">1 branch, unlimited products</p>
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedPlan("pro_growth")}
                  className={`rounded-lg border-2 p-4 text-left transition-colors ${
                    selectedPlan === "pro_growth"
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-muted-foreground/50"
                  }`}
                >
                  <p className="font-bold">Pro Growth</p>
                  <p className="text-xs text-muted-foreground">3 branches, multi-location</p>
                </button>
              </div>

              <div data-testid="paywall-pricing-toggle" className="flex items-center gap-2 p-2 rounded-lg bg-muted/50 border border-border">
                <button
                  type="button"
                  data-testid="pricing-toggle-monthly"
                  onClick={() => setIsAnnual(false)}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${!isAnnual ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  data-testid="pricing-toggle-annual"
                  onClick={() => setIsAnnual(true)}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${isAnnual ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                >
                  Annual
                </button>
              </div>

              {showFounding && (
                <label data-testid="paywall-founding-member-banner" className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useFounding}
                    onChange={(e) => setUseFounding(e.target.checked)}
                    className="rounded border-input"
                  />
                  <span>
                    Founding member pricing ({foundingSpots} spots left) — locked for life
                  </span>
                </label>
              )}

              <div className="flex items-baseline gap-2 flex-wrap">
                <span data-testid={isAnnual ? "paywall-annual-price" : "paywall-monthly-price"} className="text-2xl font-bold">
                  {selectedPlan === "pro_solo"
                    ? isAnnual
                      ? formatK(PRO_SOLO_ANNUAL)
                      : formatK(effectiveFounding ? PRO_SOLO_FOUNDING_MONTHLY : PRO_SOLO_MONTHLY)
                    : isAnnual
                      ? formatK(PRO_GROWTH_ANNUAL)
                      : formatK(effectiveFounding ? PRO_GROWTH_FOUNDING_MONTHLY : PRO_GROWTH_MONTHLY)}
                </span>
                <span className="text-sm text-muted-foreground">
                  {isAnnual ? "/year" : "/month"}
                </span>
                {isAnnual && !effectiveFounding && (
                  <span data-testid="paywall-annual-savings" className="text-xs font-bold text-emerald-600 dark:text-emerald-400 ml-auto">
                    Save {formatK(selectedPlan === "pro_solo" ? PRO_SOLO_ANNUAL_SAVINGS : PRO_GROWTH_ANNUAL_SAVINGS)}
                  </span>
                )}
              </div>

              <Button
                data-testid="paywall-cta"
                className="w-full font-semibold"
                size="lg"
                onClick={handleUnlock}
                disabled={paymentLoading}
              >
                {paymentLoading ? "Processing…" : copy.ctaLabel}
              </Button>

              {proCount > 0 && (
                <p data-testid="paywall-social-proof" className="text-xs text-center text-muted-foreground">
                  {proCount > 1
                    ? `Join ${proCount} Zambian retailers already on Pro.`
                    : "Join Zambian retailers already on Pro."}
                </p>
              )}

              <button
                type="button"
                data-testid="paywall-transparency-link"
                onClick={() => setTransparencyOpen(true)}
                className="w-full text-xs text-muted-foreground hover:text-foreground hover:underline"
              >
                How did we calculate this?
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <TransparencyModal open={transparencyOpen} onOpenChange={setTransparencyOpen} />
    </>
  );
}
